import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiJson } from '../../utils/api-client';

interface CrudHooksOptions<T extends object> {
  /** Base API path, e.g. `/api/experiences` */
  endpoint: string;
  /** TanStack Query key constant to invalidate after mutations */
  queryKey: string;
  /** Extracts the resource ID from an entity, used for update and delete URLs */
  getId: (entity: T) => string;
}

/**
 * Factory that produces `useCreate`, `useUpdate`, and `useDelete` mutation hooks
 * for a standard REST resource. All three hooks accept `{ onSuccess }` and
 * invalidate `queryKey` on success.
 */
export function createCrudHooks<T extends object>({
  endpoint,
  queryKey,
  getId,
}: CrudHooksOptions<T>) {
  const useCreate = ({ onSuccess }: { onSuccess: () => void }) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (data: T) => {
        return apiJson(endpoint, {
          method: 'POST',
          body: JSON.stringify(data),
        });
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      },
    });
  };

  const useUpdate = ({ onSuccess }: { onSuccess: () => void }) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (data: T) => {
        return apiJson(`${endpoint}/${getId(data)}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      },
    });
  };

  const useDelete = ({ onSuccess }: { onSuccess: () => void }) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (id: string) => {
        return apiJson(`${endpoint}/${id}`, { method: 'DELETE' });
      },
      onSuccess: () => {
        onSuccess();
        queryClient.invalidateQueries({ queryKey: [queryKey] });
      },
    });
  };

  return { useCreate, useUpdate, useDelete };
}
